import {useCallback} from 'react';

import {SECTION_J_PLUS, SECTION_W} from '@/constants/sections';
import {DocumentArrayGroup, DocumentData} from '@/engine';
import {collectAllArrayFields, findMaxArrayIndex} from '@/engine/utils/array-fields';
import {J_INVESTMENT_SUM_FIELDS, SEEDABLE_J_ARRAYS, seededFromCompMarkerField} from '@/model/helpers/section-j';
import {W_INVESTMENT_SUM_FIELD} from '@/model/helpers/section-w';
import {BATCH_OPERATIONS, BatchUpdate} from '@/types/document/store';
import {useDocumentContext} from '../context/use-document-context';

// Extra fields unset when a section's arrays are cleared
// Especially computed values that should be recomputed after their dependent arrays are cleared.
const SECTION_DEPENDENT_FIELDS: Partial<Record<string, string[]>> = {
  [SECTION_W]: [W_INVESTMENT_SUM_FIELD],
  [SECTION_J_PLUS]: [...SEEDABLE_J_ARRAYS.map(seededFromCompMarkerField), ...J_INVESTMENT_SUM_FIELDS],
};

const isDocumentArrayGroup = (group: {id: string}): group is DocumentArrayGroup => 'rows' in group;

const collectArrayGroupFieldIds = (data: DocumentData['data'], group: DocumentArrayGroup): string[] => {
  const count = Number(data[`_${group.id}[count]`]);
  const maxIndex = findMaxArrayIndex(data, group.id, group);
  const iterationLimit = Math.max(isNaN(count) ? 0 : count, maxIndex + 1);
  const fieldIds = collectAllArrayFields(data, group, iterationLimit - 1);

  const metaFieldIds: string[] = [];
  for (let index = 0; index < iterationLimit; index++) {
    metaFieldIds.push(
      `_${group.id}#${index}[draft]`,
      `_${group.id}#${index}[new]`,
      `_${group.id}#${index}[open]`,
      `_${group.id}#${index}[fields]`,
    );
  }

  return [...fieldIds, ...metaFieldIds, `_${group.id}[count]`];
};

export const useClearArrayFields = (): ((sectionIds: string[]) => void) => {
  const {store, documentModel} = useDocumentContext();

  return useCallback(
    (sectionIds: string[]): void => {
      const state = store.getState();

      const fieldIds = sectionIds.flatMap(sectionId => {
        const section = documentModel.sections.find(section => section.id === sectionId);
        if (!section) return [];

        const arrayFieldIds = section.groups
          .filter(isDocumentArrayGroup)
          .flatMap(group => collectArrayGroupFieldIds(state.data, group));

        return [...arrayFieldIds, ...(SECTION_DEPENDENT_FIELDS[sectionId] ?? [])];
      });

      if (fieldIds.length > 0) {
        state.batchUpdate(fieldIds.map((id): BatchUpdate => ({type: BATCH_OPERATIONS.UNSET, id})));
      }

      sectionIds.forEach(sectionId => {
        state.setSectionValidity(sectionId, false, []);
      });
    },
    [store, documentModel],
  );
};
